import { useEffect, useState, useCallback } from "react";
import type { FC } from "react";
import { Badge, Table, Spinner, TableHead, TableHeadCell, TableBody, TableRow, TableCell } from "flowbite-react";
import { supabase, TABLES } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface Worker {
  id: number;
  full_name: string;
}

interface ContractType {
  id: number;
  type: string;
}

interface Contract {
  id: number;
  created_at: string;
  contract_number: string;
  status: string;
  worker: Worker;
  contract_type: ContractType;
}

interface TransformedContract
  extends Omit<Contract, "worker" | "contract_type"> {
  worker: { full_name: string } | null;
  contract_type: { type: string };
}

const getStatusTranslation = (status: string): string => {
  // Status is already in German, just return it if it matches known values
  switch (status) {
    case "Aktiv":
    case "Storniert":
    case "Genehmigt":
    case "Unterschrieben":
    case "Warten auf QC":
    case "Ausstehende Ausführung":
    case "Verlängerung ausstehend":
      return status;
    default:
      return "Unbekannt";
  }
};

const LatestTransactions: FC = function LatestTransactions() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<TransformedContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchLatestContracts = useCallback(async () => {
    try {
      setError(null);
      if (!user) {
        setLoading(false);
        return;
      }

      const { data: workerData, error: workerError } = await supabase
        .from("workers")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (workerError) throw workerError;

      const { data, error } = await supabase
        .from("contracts")
        .select(
          `
          id,
          created_at,
          contract_number,
          status,
          worker:workers!worker_id (
            id,
            full_name
          ),
          contract_type:contract_types!contract_type_id (
            id,
            type
          )
        `
        )
        .eq("worker_id", workerData.id)
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;

      const transformedData = (data || []).map((contract: any) => ({
        id: contract.id,
        created_at: contract.created_at,
        contract_number: contract.contract_number || `CNT-${contract.id}`,
        status: contract.status,
        worker: {
          full_name: contract.worker?.full_name || "Unbekannter Mitarbeiter",
        },
        contract_type: {
          type: contract.contract_type?.type || "Unbekannter Typ",
        },
      }));

      setContracts(transformedData);
    } catch (err) {
      console.error("Error fetching contracts:", err);
      setError("Verträge konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    let isSubscribed = true;

    // Set up real-time subscription for contract updates
    const contractSubscription = supabase
      .channel("contract_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: TABLES.CONTRACTS,
        },
        () => {
          console.log("Contract data changed, refreshing...");
          if (isSubscribed) {
            fetchLatestContracts().catch(console.error);
          }
        }
      )
      .subscribe();

    // Initial fetch
    if (isSubscribed) {
      fetchLatestContracts().catch(console.error);
    }

    // Cleanup function
    return () => {
      isSubscribed = false;
      contractSubscription.unsubscribe().catch(console.error);
    };
  }, [fetchLatestContracts]);

  return (
    <div className="rounded-lg bg-white p-4 shadow-md transition-shadow hover:shadow-lg dark:bg-gray-800 sm:p-6 xl:p-8">
      <div className="mb-4 flex flex-col gap-2 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white md:text-2xl">
            Neueste Verträge
          </h3>
          <span className="text-base font-normal text-gray-600 dark:text-gray-400">
            Dies ist eine Liste der neuesten Verträge
          </span>
        </div>
        <button
          onClick={() => fetchLatestContracts()}
          disabled={loading}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white p-3 text-gray-500 shadow-md transition-all hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300 dark:focus:ring-gray-600 sm:ml-4"
          title="Daten aktualisieren"
        >
          <svg
            className={`h-6 w-6 ${loading ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="overflow-x-auto rounded-lg">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow-md sm:rounded-lg">
              <Table
                striped
                className="min-w-full divide-y divide-gray-200 dark:divide-gray-600 dark:bg-gray-800 dark:text-gray-400"
                theme={{
                  root: {
                    shadow: "hidden",
                    wrapper: "relative overflow-x-auto shadow-md sm:rounded-lg",
                  },
                }}
              >
                <TableHead className="bg-gray-50 dark:bg-gray-700">
                  <TableHeadCell className="p-4 md:p-5">
                    Vertragstyp
                  </TableHeadCell>
                  <TableHeadCell className="p-4 md:p-5">
                    Mitarbeiter
                  </TableHeadCell>
                  <TableHeadCell className="p-4 md:p-5">
                    Datum & Uhrzeit
                  </TableHeadCell>
                  <TableHeadCell className="p-4 md:p-5">
                    Vertragsnummer
                  </TableHeadCell>
                  <TableHeadCell className="p-4 md:p-5">Status</TableHeadCell>
                </TableHead>
                <TableBody className="bg-white dark:bg-gray-800">
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        <div className="flex items-center justify-center p-4 md:p-5">
                          <Spinner size="xl" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="p-4 text-center text-red-500 md:p-5"
                      >
                        {error}
                      </TableCell>
                    </TableRow>
                  ) : contracts.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="p-4 text-center md:p-5"
                      >
                        Keine Verträge gefunden
                      </TableCell>
                    </TableRow>
                  ) : (
                    contracts.map((contract) => (
                      <TableRow
                        key={contract.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <TableCell className="whitespace-nowrap p-4 text-sm font-normal text-gray-900 dark:text-white md:p-5 md:text-base">
                          <span className="text-gray-900 dark:text-white">{contract.contract_type.type || "Unbekannter Typ"}</span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap p-4 text-sm font-normal text-gray-900 dark:text-white md:p-5 md:text-base">
                          <span className="font-semibold">
                            {contract.worker?.full_name ||
                              "Unbekannter Mitarbeiter"}
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap p-4 text-sm font-normal text-gray-500 dark:text-gray-400 md:p-5 md:text-base">
                          <span className="text-gray-900 dark:text-white">{new Date(contract.created_at).toLocaleDateString()}</span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap p-4 text-sm font-normal text-gray-900 dark:text-white md:p-5 md:text-base">
                          {contract.contract_number || `CNT-${contract.id}`}
                        </TableCell>
                        <TableCell className="flex whitespace-nowrap p-4 md:p-5">
                          <Badge
                            className="text-sm md:text-base"
                            color={
                              contract.status === "Aktiv"
                                ? "success"
                                : contract.status === "Storniert"
                                ? "failure"
                                : contract.status === "Genehmigt"
                                ? "info"
                                : contract.status === "Unterschrieben"
                                ? "success"
                                : contract.status === "Warten auf QC"
                                ? "warning"
                                : contract.status === "Ausstehende Ausführung"
                                ? "warning"
                                : contract.status === "Verlängerung ausstehend"
                                ? "purple"
                                : "gray"
                            }
                          >
                            {getStatusTranslation(contract.status) ||
                              "Unbekannt"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between pt-4 sm:pt-6">
        <div className="shrink-0">
          <button
            type="button"
            onClick={() => navigate("/customers/overview")}
            className="inline-flex items-center rounded-lg p-3 text-sm font-medium uppercase text-primary-700 transition-colors hover:bg-gray-100 dark:text-primary-500 dark:hover:bg-gray-700 md:p-4 md:text-base"
          >
            Vertragsbericht
            <svg
              className="ml-2 h-5 w-5 md:h-6 md:w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LatestTransactions;
